import React, { useState, useEffect } from 'react';
import { productAPI } from '../api';
import { useCart } from '../context/CartContext';
import logo from '../assets/logo.jpeg'; // Adjust the path based on your logo location

const Products = () => {
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [addedProducts, setAddedProducts] = useState({});

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, [selectedCategory, search, currentPage]);

  const fetchCategories = async () => {
    try {
      const response = await productAPI.getCategories();
      setCategories(response.data.categories);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = {
        category: selectedCategory,
        search: search,
        page: currentPage,
        limit: 12,
      };

      const response = await productAPI.getAllProducts(params);
      setProducts(response.data.products);
      setTotalPages(response.data.totalPages);
      setError('');
    } catch (err) {
      setError('Failed to load products');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleAddToCart = (product) => {
    addToCart(product, 1);
    setAddedProducts((prev) => ({
      ...prev,
      [product._id]: true,
    }));
    
    setTimeout(() => {
      setAddedProducts((prev) => ({
        ...prev,
        [product._id]: false,
      }));
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-[#F2F2F2] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-3" style={{ color: '#0F2F3D' }}>
            Desi-Mart Products
          </h1>
          <p className="text-lg" style={{ color: '#333333' }}>
            Discover premium Ayurvedic & FMCG products for your wellness journey
          </p>
          <div className="mt-3 inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#E4572E' }}></span>
            <span className="text-sm" style={{ color: '#333333' }}>
              Price Range: ₹310 - ₹31,000
            </span>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-10">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search products..."
                value={search}
                onChange={handleSearch}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none transition-all duration-300"
                style={{ 
                  borderColor: search ? '#E4572E' : '#E5E7EB',
                  boxShadow: search ? '0 0 0 3px rgba(228, 87, 46, 0.1)' : 'none'
                }}
              />
            </div>
            <div className="sm:w-64">
              <select 
                value={selectedCategory} 
                onChange={handleCategoryChange} 
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none transition-all duration-300 bg-white cursor-pointer"
                style={{ 
                  borderColor: selectedCategory ? '#E4572E' : '#E5E7EB',
                  color: '#333333'
                }}
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-8 p-4 bg-red-50 border-l-4 rounded-lg" style={{ borderColor: '#E4572E' }}>
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, idx) => (
              <div key={idx} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="animate-pulse">
                  <div className="h-48 bg-gray-200"></div>
                  <div className="p-5 space-y-3">
                    <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-10 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🔍</div>
            <p className="text-xl" style={{ color: '#333333' }}>No products found</p>
            <p className="text-gray-500 mt-2">Try adjusting your search or filter</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map((product) => (
                <div 
                  key={product._id} 
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
                >
                  {/* Image Container with Logo Fallback */}
                  <div className="relative h-48 overflow-hidden bg-gradient-to-br from-[#0F2F3D] to-[#1a4a5f]">
                    {product.image ? (
                      <img 
                        src={product.image} 
                        alt={product.name} 
                        className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center p-6">
                        <img 
                          src={logo} 
                          alt="Desi-Mart Logo" 
                          className="w-32 h-32 object-contain opacity-90 transform transition-transform duration-500 group-hover:scale-110"
                        />
                      </div>
                    )}
                    {!product.isActive && (
                      <div className="absolute top-3 right-3">
                        <span className="px-3 py-1 text-xs font-semibold rounded-full" 
                          style={{ 
                            backgroundColor: '#F47C4D',
                            color: 'white'
                          }}>
                          Coming Soon
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="mb-3">
                      <h3 className="text-lg font-bold mb-1 line-clamp-2" style={{ color: '#0F2F3D' }}>
                        {product.name}
                      </h3>
                      <p className="text-xs uppercase tracking-wider" style={{ color: '#E4572E' }}>
                        {product.category?.replace(/_/g, ' ')}
                      </p>
                    </div>

                    <p className="text-sm mb-3 line-clamp-2" style={{ color: '#333333' }}>
                      {product.description}
                    </p>

                    {product.benefits && product.benefits.length > 0 && (
                      <div className="mb-3 flex flex-wrap gap-1">
                        {product.benefits.slice(0, 2).map((benefit, index) => (
                          <span 
                            key={index}
                            className="text-xs px-2 py-1 rounded-full"
                            style={{ 
                              backgroundColor: '#F2F2F2',
                              color: '#333333'
                            }}
                          >
                            {benefit}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex justify-between items-center mb-4">
                      <div className="text-xl font-bold" style={{ color: '#E4572E' }}>
                        {product.price > 0 ? `₹${product.price?.toLocaleString()}` : 'TBD'}
                      </div>
                      {product.rating > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="text-yellow-400">★</span>
                          <span className="text-sm font-medium" style={{ color: '#333333' }}>
                            {product.rating}
                          </span>
                        </div>
                      )}
                    </div>

                    <button 
                      onClick={() => handleAddToCart(product)}
                      disabled={!product.isActive}
                      className="w-full py-3 px-4 rounded-lg font-medium transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
                      style={{ 
                        backgroundColor: !product.isActive ? '#E5E7EB' : addedProducts[product._id] ? '#10B981' : '#E4572E',
                        color: !product.isActive ? '#9CA3AF' : 'white',
                        boxShadow: !product.isActive ? 'none' : '0 4px 6px -1px rgba(228, 87, 46, 0.2)'
                      }}
                    >
                      {!product.isActive ? (
                        'Coming Soon'
                      ) : addedProducts[product._id] ? (
                        <span className="flex items-center justify-center gap-2">
                          <span>✓</span> Added to Cart
                        </span>
                      ) : (
                        'Add to Cart'
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-4 mt-12">
                <button 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="px-6 py-3 rounded-lg font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                  style={{ 
                    backgroundColor: currentPage === 1 ? '#E5E7EB' : '#0F2F3D',
                    color: 'white'
                  }}
                >
                  Previous
                </button>
                <span className="px-4 py-2 rounded-lg font-semibold" style={{ color: '#0F2F3D' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button 
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="px-6 py-3 rounded-lg font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                  style={{ 
                    backgroundColor: currentPage === totalPages ? '#E5E7EB' : '#0F2F3D',
                    color: 'white'
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Products;